/**
 * OutcomeKpiStrip — replaces the activity tiles with the four scheduling
 * KPIs that move margin per crew-day. See data/scheduling/outcomeMetrics.ts
 * for the math and bands.
 *
 * Tiles are now clickable: click → opens KpiDrilldownDrawer with the
 * underlying jobs/crews dragging the metric.
 */
import { useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { computeOutcomeMetrics, type OutcomeMetric } from "@/data/scheduling/outcomeMetrics";
import { cn } from "@/lib/utils";
import { TrendingUp, CheckCircle2, MapPin, RefreshCcw, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { KpiDrilldownDrawer } from "./KpiDrilldownDrawer";

const ICONS: Record<OutcomeMetric["key"], React.ElementType> = {
  utilization: TrendingUp,
  first_time_complete: CheckCircle2,
  travel_share: MapPin,
  reschedule: RefreshCcw,
};

const TOOLTIPS: Record<OutcomeMetric["key"], string> = {
  utilization: "Booked install crew-days ÷ available crew-days. Target ≥ 75%. Click to see under/overbooked crews.",
  first_time_complete: "Wraps marked complete on first visit ÷ total wraps. Target ≥ 90%. Click to see incomplete wraps.",
  travel_share: "Jobs scheduled outside each crew's home city ÷ total scheduled jobs. Target ≤ 20%. Click for the offenders.",
  reschedule: "Jobs whose install date moved at least once ÷ total scheduled jobs. Target ≤ 8%. Click to see what moved.",
};

export function OutcomeKpiStrip() {
  const { jobs, installerWrapRecords } = useAppData();
  const [activeKey, setActiveKey] = useState<OutcomeMetric["key"] | null>(null);

  const metrics = useMemo(
    () => computeOutcomeMetrics({ jobs, wraps: installerWrapRecords }),
    [jobs, installerWrapRecords],
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border/40 border-y border-border/40">
        {metrics.map(m => (
          <Tile key={m.key} metric={m} onOpen={() => setActiveKey(m.key)} />
        ))}
      </div>
      <KpiDrilldownDrawer
        open={activeKey !== null}
        metricKey={activeKey}
        onClose={() => setActiveKey(null)}
      />
    </TooltipProvider>
  );
}

function Tile({ metric, onOpen }: { metric: OutcomeMetric; onOpen: () => void }) {
  const Icon = ICONS[metric.key];
  const display = metric.value === null ? "—" : `${metric.value}%`;
  const direction = metric.higherIsBetter ? "≥" : "≤";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            "group relative px-4 py-3 text-left transition-colors cursor-pointer",
            "hover:bg-muted/20",
            "focus:outline-none focus:bg-muted/30",
          )}
        >
          <div className="flex items-center justify-between text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-[0.06em] font-medium">{metric.label}</span>
            </div>
            <ChevronRight className="h-3 w-3 opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className={cn(
              "text-lg font-semibold tabular-nums",
              metric.tone === "good"   ? "text-success" :
              metric.tone === "warn"   ? "text-warning" :
              metric.tone === "bad"    ? "text-destructive" :
                                          "text-foreground",
            )}>
              {display}
            </span>
            <span className="text-[10px] text-muted-foreground/70">
              target {direction} {metric.target}%
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">{metric.sub}</p>
          <p className="text-[9.5px] text-primary/70 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            View breakdown →
          </p>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs max-w-[280px]">
        {TOOLTIPS[metric.key]}
      </TooltipContent>
    </Tooltip>
  );
}